import { Form, Formik } from 'formik'
import type { ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import cc from 'classcat'

import type { FormState } from './themes/shared'
import { ThemeFilterTagsList } from './themes/ThemeFilterTagsList'
import { ThemeFilters } from './themes/ThemeFilters'
import { ThemesList } from './themes/ThemesList'
import { getFilteredThemes } from './themes/shared'
import { switchTheme } from '../../store/actions/theme'
import { useTranslate } from '../../utils/translate'
import { withQueryAndScope } from '../../urlGeneratorsPlain'
import Button from '../../components/formComponents/Button'
import ButtonRow from '../../components/formComponents/ButtonRow'

export default function Themes(): ReactElement {
  const themes = useSelector<State, Core.Theme[]>((state: State) => state.get('themes').toJS())
  const currentTheme = useSelector<State, Core.Theme>((state: State) => state.getIn(['shop', 'theme']).toJS())
  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const t = useTranslate('interface', 'views.editorThemesView')

  const initialFormValues: FormState = { selectedThemeId: currentTheme.id, selectedTags: [] }

  const dispatch = useDispatch<GlobalDispatch>()
  const location = useLocation()
  const navigate = useNavigate()

  function handleFormSubmit(values: FormState) {
    if (values.selectedThemeId !== currentTheme.id) {
      const selectedTheme = themes.find((theme) => theme.id === values.selectedThemeId)

      if (selectedTheme) {
        dispatch(switchTheme(selectedTheme))

        navigate(withQueryAndScope('/', location))
        // Auto-checks the "Select a theme" to-do in the BYD cockpit's dashboard checklist and setup guide
        window.parent?.postMessage(
          {
            type: 'CHECKLIST',
            payload: {
              websiteOnly: 'designSettings.themeSelection',
              regularShop: 'designSettings.themeSelection',
            },
          },
          mboBaseUrl,
        )
      }
    }
  }

  return (
    <Formik initialValues={initialFormValues} onSubmit={handleFormSubmit}>
      {({ values, setFieldValue }) => {
        const filteredThemes = getFilteredThemes(themes, values.selectedTags)
        const resetThemeSelection = () => setFieldValue('selectedThemeId', initialFormValues.selectedThemeId)

        if (
          values.selectedThemeId !== initialFormValues.selectedThemeId &&
          !filteredThemes.some((theme) => theme.id === values.selectedThemeId)
        ) {
          resetThemeSelection()
        }

        return (
          <Form className={cc(['ep-theme-manager-container', isBeyondShop && 'ep-theme-manager-intercom-padding'])}>
            <h1 className="ep-theme-manager-headline">{t('title')}</h1>
            <p className="ep-theme-manager-description">{t('description', { joinArrays: '\n' })}</p>
            <h2 className="ep-theme-manager-filters-headline">
              {t('filters.title', { activeFiltersCount: values.selectedTags.length })}
            </h2>

            <div className="ep-theme-manager-filters-tags">
              <ThemeFilterTagsList />
            </div>

            <div className="ep-theme-manager-sidebar">
              <ThemeFilters />
            </div>

            <div className="ep-theme-manager-main">
              <scroll-shadow class="ep-theme-manager-main-content">
                <div className="ep-theme-manager-main-content-root">
                  {filteredThemes.length ? (
                    <ThemesList themes={filteredThemes} />
                  ) : (
                    <div className="ep-theme-manager-empty-state">
                      <h3>{t('emptyState.headline')}</h3>
                      <p>{t('emptyState.infoText')}</p>
                    </div>
                  )}
                </div>
              </scroll-shadow>
              {values.selectedThemeId !== initialFormValues.selectedThemeId && (
                <ButtonRow>
                  <Button type="button" variant="cancel" onClick={() => resetThemeSelection()}>
                    {t('cancelButton.label')}
                  </Button>
                  <Button type="submit">{t('saveButton.label')}</Button>
                </ButtonRow>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

Themes.contentCreationDisabled = () => true
